import { useMutation } from '@apollo/client/react';
import {
  PROCESS_ESNI_MEDIA,
  VALIDATE_ESNI_MEDIA,
} from '@optools/services/operator-gateway/esni/mutations';
import { ESNIMediaForm } from '@optools/tools/esni/components/rules/process/esni-media-form';
import { PoliciesCard } from '@optools/tools/esni/components/rules/process/policies-card';
import { RulesCard } from '@optools/tools/esni/components/rules/process/rules-card';
import { querySchema } from '@optools/tools/esni/schemas/process';
import { createFileRoute, stripSearchParams, useNavigate, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import { toast } from 'sonner';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_auth/esni/rules/process')({
  component: RouteComponent,
  validateSearch: zodValidator(querySchema),
  search: {
    middlewares: [stripSearchParams({ mediaId: '' })],
  },
});
/* eslint-enable perfectionist/sort-objects */

export function RouteComponent() {
  const { mediaId } = useSearch({ from: '/_auth/esni/rules/process' });
  const navigate = useNavigate({ from: Route.fullPath });

  const [
    validateMedia,
    {
      called: validationCalled,
      data: validationData,
      error: validationError,
      loading: validating,
      reset: resetValidation,
    },
  ] = useMutation(VALIDATE_ESNI_MEDIA);
  const [
    processMedia,
    {
      called: processingCalled,
      data: processingData,
      error: processingError,
      loading: processing,
      reset: resetProcessing,
    },
  ] = useMutation(PROCESS_ESNI_MEDIA, {
    onCompleted: () => {
      toast.success('Media processed successfully');
    },
  });

  useEffect(() => {
    if (mediaId) validateMedia({ variables: { mediaId } });
  }, [mediaId, validateMedia]);

  const handleValidateMedia = async (newMediaId: string) => {
    resetValidation();
    resetProcessing();
    if (mediaId !== newMediaId) {
      navigate({ replace: true, search: (params) => ({ ...params, mediaId: newMediaId }) });
    } else {
      validateMedia({ variables: { mediaId: newMediaId } });
    }
  };

  const handleProcessMedia = () => {
    processMedia({ variables: { mediaId } });
  };

  const validated = validationCalled && !validating && !validationError;
  const processed = processingCalled && !processing && !processingError;
  const loading = validating || processing;

  const rules =
    processingData?.processEsniMedia?.rules ?? validationData?.validateEsniMedia?.rules ?? [];
  const policies =
    processingData?.processEsniMedia?.policies ?? validationData?.validateEsniMedia?.policies ?? [];

  return (
    <div className="grid grid-rows-[auto_1fr] gap-12 md:h-full md:overflow-auto">
      <div>
        <h1 className="mb-2 text-lg font-semibold">Validate & Process ESNI Media</h1>
        <p className="text-sm">
          Validate the rules and viewing policies that will be attached to the given media, then
          reprocess the media to attach the latest policies
        </p>
      </div>
      <div className="mx-auto grid w-full max-w-7xl content-start gap-4 md:max-h-[min(100%,800px)] md:grid-cols-2 md:overflow-hidden md:pb-12">
        <div className="flex flex-col gap-4">
          <ESNIMediaForm
            mediaId={mediaId}
            processed={processed}
            processing={processing}
            validated={validated}
            validating={validating}
            onProcess={handleProcessMedia}
            onValidate={handleValidateMedia}
          />
          <RulesCard loading={loading} rules={rules} />
        </div>
        <PoliciesCard loading={loading} policies={policies} />
      </div>
    </div>
  );
}
